// features/userdashboard/userdashboard.slice.ts
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchUserDashboardApi, fetchUserDashboardWithCustomParams } from './userdashboard.service';
import { UserDashboardParams } from '../../api/userdashboard/index';
import { logOut } from '../../../redux/auth/authSlice';
import { notify } from '../../helper/notify';
import i18n from '../../../translation/i18n';

export const fetchUserDashboard = createAsyncThunk(
  'userDashboard/fetchUserDashboard',
  async (payload: UserDashboardParams) => {
    try {
      const data = await fetchUserDashboardApi(payload);
      return data;
    } catch (error: any) {
      notify.error(error?.response?.data?.message || error?.message || (i18n.t('SOMETHING_WENT_WRONG') as string));
      throw error;
    }
  }
);

export const fetchUserDashboardWithParams = createAsyncThunk(
  'userDashboard/fetchUserDashboardWithParams',
  async (payload: Record<string, any>) => {
    try {
      const data = await fetchUserDashboardWithCustomParams(payload);
      return data;
    } catch (error: any) {
      notify.error(error?.response?.data?.message || error?.message || (i18n.t('SOMETHING_WENT_WRONG') as string));
      throw error;
    }
  }
);

type Awaited<T> = T extends Promise<infer U> ? U : T;

type UserDashboardState = {
  message: string | null;
  dashboardData: Awaited<ReturnType<typeof fetchUserDashboardApi>> | null;
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
  filters: {
    dateTime: string;
    type?: number;
    status?: number;
  };
};

const initialState: UserDashboardState = {
  message: null,
  dashboardData: null,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },
  filters: {
    dateTime: '',
    type: undefined,
    status: undefined,
  },
};

const userDashboardSlice = createSlice({
  name: 'userDashboard',
  initialState,
  reducers: {
    clearDashboardData: (state) => {
      state.dashboardData = null;
      state.error = null;
      state.message = null;
    },
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1; // Reset to first page when page size changes
    },
    setDateTime: (state, action) => {
      state.filters.dateTime = action.payload;
      state.pagination.currentPage = 1; // Reset to first page when filter changes
    },
    clearDateTime: (state) => {
      state.filters.dateTime = '';
    },
    setType: (state, action) => {
      state.filters.type = action.payload;
      state.pagination.currentPage = 1; // Reset to first page when filter changes
    },
    setStatus: (state, action) => {
      state.filters.status = action.payload;
      state.pagination.currentPage = 1; // Reset to first page when filter changes
    },
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
  },
  extraReducers: builder => {
    builder
      // Fetch user dashboard cases
      .addCase(fetchUserDashboard.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserDashboard.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        
        // Store the complete response directly
        state.dashboardData = payload;
        
        // Update pagination info based on actual response
        const totalPages = payload.numberOfPages || Math.ceil((payload.totalCount || 0) / state.pagination.pageSize);
        state.pagination = {
          ...state.pagination,
          totalCount: payload.totalCount || 0,
          totalPages: totalPages,
          hasNextPage: state.pagination.currentPage < totalPages,
          hasPreviousPage: state.pagination.currentPage > 1,
        };
        
        state.message = payload.message || 'Dashboard data fetched successfully!';
      })
      .addCase(fetchUserDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboard data';
        state.dashboardData = null;
      })

      // Fetch user dashboard with custom params cases
      .addCase(fetchUserDashboardWithParams.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserDashboardWithParams.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        
        state.dashboardData = payload;
        
        // Update pagination info based on actual response
        const totalPages = payload.numberOfPages || Math.ceil((payload.totalCount || 0) / state.pagination.pageSize);
        state.pagination = {
          ...state.pagination,
          totalCount: payload.totalCount || 0,
          totalPages: totalPages,
          hasNextPage: state.pagination.currentPage < totalPages,
          hasPreviousPage: state.pagination.currentPage > 1,
        };
        
        state.message = payload.message || 'Dashboard data fetched successfully!';
      })
      .addCase(fetchUserDashboardWithParams.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch dashboard data';
        state.dashboardData = null;
      })
      // Reset state on logout
      .addCase(logOut, () => initialState);
  },
});

export const { 
  clearDashboardData, 
  setCurrentPage, 
  setPageSize, 
  setDateTime,
  clearDateTime,
  setType,
  setStatus,
  clearError, 
  clearMessage 
} = userDashboardSlice.actions;

export default userDashboardSlice.reducer;